import { GetImageStore } from "SessionState/Image/ImageStore";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";

interface ImageRenderProps {
    src: string | null | undefined;
    alt: string;
    className?: string;
    containerName: string;
    onClick?: () => void;
}

const imageCache = new Map<string, string>(); // Local image cache
const FALLBACK_IMAGE = "/img/profile/imagePlaceholder_1.png"; // Fallback placeholder image




const ImageRender = observer(
    ({ src, alt, className = "img-fluid rounded", onClick, containerName }: ImageRenderProps) => {
        const [image, setImage] = useState<string>(FALLBACK_IMAGE); // Start with fallback
        const [isLoading, setIsLoading] = useState<boolean>(false); // Simplified loading state
        const imageStore = GetImageStore();

        useEffect(() => {
            const loadImage = async () => {
                if (!src) {
                    // If no src is provided, fallback image is used
                    setImage(FALLBACK_IMAGE);
                    return;
                }

                if (imageCache.has(src)) {
                    // Use cached image if available
                    setImage(imageCache.get(src)!);
                    return;
                }

                try {
                    setIsLoading(true);
                    const loadedImage = await imageStore.getImage(src, containerName);

                    if (loadedImage) {
                        imageCache.set(src, loadedImage); // Cache the loaded image
                        setImage(loadedImage);
                    } else {
                        throw new Error("Failed to load image");
                    }
                } catch (error) {
                    console.error("Error loading image:", error);
                    setImage(FALLBACK_IMAGE); // Fallback on error
                } finally {
                    setIsLoading(false);
                }
            };

            loadImage();
        }, [src, containerName, imageStore]);

        return (
            <>
                {isLoading && (
                    <div className="d-flex justify-content-center">
                        <Spinner animation="grow" variant="primary" />
                    </div>
                )}
                <img
                    src={image}
                    alt={alt || "Image placeholder"}
                    className={`${className} ${isLoading ? "d-none" : "d-block"}`}
                    onClick={onClick}
                />
            </>
        );
    }
);

export default ImageRender;
